/**
 * <traveling-carousel> - slides dissolve/crossfade between each other.
 *
 * Deliberately NOT a native scrollable track (overflow-x: auto /
 * scroll-snap): that pattern renders every slide side-by-side in the DOM
 * at full size and lets the browser's own scroll position drive things,
 * which is what caused the "empty scroll" bug in the first attempt (users
 * could scroll into blank space past the last real slide). Here every
 * slide is stacked in the same spot (`position: absolute; inset: 0`) and
 * this script just toggles `aria-hidden`, which drives an opacity
 * transition in CSS - fully programmatic, nothing to scroll past.
 *
 * Slides stay in normal layout (opacity, not display: none) and load
 * eagerly, so an inactive slide's image is always ready before it fades
 * in - this is what previously fixed a bug where a slide navigated away
 * from and back to would show broken alt text instead of its image.
 */
class TravelingCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    // querySelectorAll, not querySelector - there are two pairs of these
    // now (one flanking the viewport for desktop, one below it with the
    // dots for tablet/mobile - see traveling-letter.liquid), only one
    // pair visible at a time via CSS. querySelector would've only ever
    // wired up whichever pair happens to come first in the DOM.
    this.prevButtons = Array.from(this.querySelectorAll('[data-prev]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-next]'));
    this.index = 0;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dot)));
    });

    this.prevButtons.forEach((button) => {
      button.addEventListener('click', () => this.goTo(this.index - 1));
    });
    this.nextButtons.forEach((button) => {
      button.addEventListener('click', () => this.goTo(this.index + 1));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.goTo(this.index + 1);
      if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
    });

    // Basic touch-swipe support.
    let touchStartX = null;
    const viewport = this.querySelector('[data-viewport]');
    if (viewport) {
      viewport.addEventListener(
        'touchstart',
        (event) => {
          touchStartX = event.touches[0].clientX;
        },
        { passive: true }
      );
      viewport.addEventListener(
        'touchend',
        (event) => {
          if (touchStartX === null) return;
          const deltaX = event.changedTouches[0].clientX - touchStartX;
          const SWIPE_THRESHOLD = 40;
          if (deltaX > SWIPE_THRESHOLD) this.goTo(this.index - 1);
          if (deltaX < -SWIPE_THRESHOLD) this.goTo(this.index + 1);
          touchStartX = null;
        },
        { passive: true }
      );
    }

    this.render();
  }

  goTo(nextIndex) {
    const count = this.slides.length;
    if (count === 0) return;
    this.index = ((nextIndex % count) + count) % count;
    this.render();
  }

  render() {
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      dot.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    });
  }
}

customElements.define('traveling-carousel', TravelingCarousel);
